/* eslint-disable @typescript-eslint/no-unused-vars */
import React, { useContext, useEffect, useState } from 'react';

import { StyleSheet, View, TouchableHighlight } from 'react-native';
import DyteClient from '@dytesdk/web-core';
import { States } from '../types/props';
import { defaultIconPack, IconPack } from '../lib/icons';
import { DyteI18n, useLanguage } from '../lib/lang';
import DyteText from './DyteText';
import DyteSettingsAudio from './DyteSettingsAudio';
import DyteSettingsVideo from './DyteSettingsVideo';
import { SvgXml } from 'react-native-svg';
import { DyteUIContext } from '../lib/context-provider/ui-context';

export default function ({
  meeting,
  size = 'sm',
  states,
  iconPack = defaultIconPack,
  t,
  onClose,
}: {
  meeting: DyteClient;
  size?: 'lg' | 'md' | 'sm' | 'xl';
  states?: States;
  iconPack?: IconPack;
  t?: DyteI18n;
  onClose?: any;
}) {
  const { storeStates } = useContext(DyteUIContext);
  const { colors } = storeStates.designSystem;
  const [audioSettings, setAudioSettings] = useState(false);
  const [videoSettings, setVideoSettings] = useState(false);
  const [networkStatus, setNetworkStatus] = useState('good');
  const defaultLang = useLanguage();
  t = t ?? defaultLang;
  iconPack = iconPack ?? defaultIconPack;
  useEffect(() => {
    const poorConnectionListener = ({ score }) => {
      if (score < 7) {
        setNetworkStatus('poor');
      } else if (score < 4) {
        setNetworkStatus('poorest');
      }
      setTimeout(() => {
        // reset after 5 seconds
        setNetworkStatus('good');
      }, 5000);
    };
    meeting.meta.addListener('poorConnection', poorConnectionListener);
    return () => {
      meeting.meta.removeListener('poorConnection', poorConnectionListener);
    };
  }, [meeting]);
  const styles = StyleSheet.create({
    dialog: {
      backgroundColor: colors.background[900],
      height: 400,
      padding: 10,
      borderRadius: 8,
    },
    wrapper: {
      marginBottom: 20,
      padding: 10,
      alignItems: 'center',
    },
    audioText: {
      color: 'white',
      textAlign: 'center',
      fontSize: 20,
    },
    videoText: {
      color: 'white',
      textAlign: 'center',
      fontSize: 20,
      marginTop: 25,
    },
    backIcon: {
      position: 'absolute',
      top: 13,
      left: 10,
      padding: 8,
    },
    button: {
      borderWidth: 1,
      borderColor: 'white',
      alignItems: 'center',
      justifyContent: 'center',
      backgroundColor: 'rgb(0,150,220)',
      borderRadius: 20,
      height: 40,
    },
    closeButton: {
      position: 'absolute',
      top: 8,
      right: 8,
      padding: 8,
    },
    closeContainer: {
      position: 'absolute',
      top: 13,
      right: 5,
    },
    connection: {
      flexDirection: 'row',
      justifyContent: 'space-between',
      alignItems: 'center',
      padding: 10,
      marginTop: 10,
    },
    audioSetting: {
      flexDirection: 'row',
      justifyContent: 'space-between',
      marginVertical: 10,
    },
    micIcon: {
      marginRight: 5,
      flexDirection: 'row',
    },
    videoIcon: {
      marginRight: 5,
      flexDirection: 'row',
    },
    videoSetting: {
      flexDirection: 'row',
      justifyContent: 'space-between',
    },
  });
  const closeButton = () => {
    return (
      <TouchableHighlight
        underlayColor={colors.background[800]}
        style={styles.closeButton}
        onPress={onClose}
      >
        <SvgXml
          xml={iconPack.dismiss.replace('currentColor', colors.text)}
          height={20}
          width={20}
        />
      </TouchableHighlight>
    );
  };
  const settingOptions = () => {
    return (
      <View>
        {meeting.self.mediaPermissions.audio === 'ACCEPTED' && (
          <TouchableHighlight
            underlayColor={colors.background[800]}
            onPress={() => {
              setAudioSettings(true);
            }}
            // eslint-disable-next-line react-native/no-inline-styles
            style={{ padding: 10 }}
          >
            <View style={styles.audioSetting}>
              {/* eslint-disable-next-line react-native/no-inline-styles */}
              <View style={{ marginLeft: 5 }}>
                <DyteText>Audio</DyteText>
              </View>
              <View style={styles.micIcon}>
                <SvgXml
                  xml={iconPack.mic_on.replace('currentColor', colors.text)}
                  height={20}
                  width={20}
                />
                {/* eslint-disable-next-line react-native/no-inline-styles */}
                <View style={{ width: 10 }} />
                <SvgXml
                  xml={iconPack.chevron_right.replace(
                    'currentColor',
                    colors.text
                  )}
                  height={20}
                  width={20}
                />
              </View>
            </View>
          </TouchableHighlight>
        )}
        {meeting.self.mediaPermissions.video === 'ACCEPTED' && (
          <TouchableHighlight
            underlayColor={colors.background[800]}
            onPress={() => {
              setVideoSettings(true);
            }}
            // eslint-disable-next-line react-native/no-inline-styles
            style={{ padding: 10 }}
          >
            <View style={styles.videoSetting}>
              {/* eslint-disable-next-line react-native/no-inline-styles */}
              <View style={{ flex: 2, marginLeft: 5 }}>
                <DyteText>Video</DyteText>
              </View>
              <View style={styles.videoIcon}>
                <SvgXml
                  xml={iconPack.video_on.replace('currentColor', colors.text)}
                  height={20}
                  width={20}
                />
                {/* eslint-disable-next-line react-native/no-inline-styles */}
                <View style={{ width: 10 }} />
                <SvgXml
                  xml={iconPack.chevron_right.replace(
                    'currentColor',
                    colors.text
                  )}
                  height={20}
                  width={20}
                />
              </View>
            </View>
          </TouchableHighlight>
        )}
        <View style={styles.connection}>
          {/* eslint-disable-next-line react-native/no-inline-styles */}
          <View style={{ marginLeft: 3, marginRight: 3 }}>
            <DyteText size="md">Connection</DyteText>
          </View>
          {/* eslint-disable-next-line react-native/no-inline-styles */}
          <View style={{ marginRight: 3 }}>
            <SvgXml
              xml={iconPack.wifi.replace('currentColor', colors.success)}
              height={20}
              width={20}
            />
          </View>
        </View>
      </View>
    );
  };
  const getStatusColor = () => {
    if (networkStatus === 'good') return 'green';
    else if (networkStatus === 'poor') return 'yellow';
    else return 'red';
  };
  return (
    <View style={styles.dialog}>
      <View style={styles.wrapper}>
        {audioSettings && (
          <DyteText size="md" fontWeight="900">
            Audio
          </DyteText>
        )}
        {videoSettings && (
          <DyteText size="md" fontWeight="900">
            Video
          </DyteText>
        )}
        {!audioSettings && !videoSettings && (
          <DyteText size="md" fontWeight="900">
            Settings
          </DyteText>
        )}
      </View>
      <View style={styles.closeContainer}>{closeButton()}</View>
      {(audioSettings || videoSettings) && (
        <TouchableHighlight
          underlayColor={colors.background[800]}
          style={styles.backIcon}
          onPress={() => {
            setAudioSettings(false);
            setVideoSettings(false);
          }}
        >
          <SvgXml
            xml={iconPack.chevron_left.replace('currentColor', colors.text)}
            height={20}
            width={20}
          />
        </TouchableHighlight>
      )}
      {audioSettings && meeting.self.mediaPermissions.audio === 'ACCEPTED' && (
        <DyteSettingsAudio meeting={meeting} />
      )}
      {videoSettings && meeting.self.mediaPermissions.video === 'ACCEPTED' && (
        <DyteSettingsVideo meeting={meeting} />
      )}
      {!audioSettings && !videoSettings && settingOptions()}
    </View>
  );
}
